import { create } from "zustand";
import {
	createDefaultFilters,
	listOrganizationTimeEntries,
	listTeamMembers,
	resolveTimeRange,
	TIME_RANGE_PRESETS,
} from "../services/timeClockApi";
import {
	buildFocusDayTimelineRows,
	buildSummaryRows,
	normalizeTimeEntries,
} from "@iq/models";

const defaultFilters = createDefaultFilters();

const useAdminTimeClockStore = create((set, get) => ({
	filters: defaultFilters,
	teamMembers: [],
	entries: [],
	summaryRows: [],
	timelineRows: [],
	hydrated: false,
	loading: false,
	error: "",

	setFilter: (key, value) => {
		set((state) => ({
			filters: {
				...state.filters,
				[key]: value,
			},
		}));
	},

	setFilters: (nextFilters) => {
		set((state) => ({
			filters: {
				...state.filters,
				...nextFilters,
			},
		}));
	},

	hydrate: async () => {
		if (get().loading) {
			return;
		}

		const { filters } = get();
		const { from, to } = resolveTimeRange(filters);
		if (!from || !to) {
			set({
				error: "Choose a valid date range",
				entries: [],
				summaryRows: [],
				timelineRows: [],
				hydrated: false,
			});
			return;
		}

		set({ loading: true, error: "" });
		try {
			const [rawEntries, members] = await Promise.all([
				listOrganizationTimeEntries({
					from,
					to,
					userId: filters.userId,
					limit: 1000,
				}),
				listTeamMembers(),
			]);

			const normalizedEntries = normalizeTimeEntries(rawEntries);
			const scopedMembers =
				filters.userId === "all"
					? members
					: (Array.isArray(members) ? members : []).filter(
						(member) => String(member?.id || "") === String(filters.userId)
					);
			const summaryRows = buildSummaryRows(scopedMembers, normalizedEntries);
			const timelineRows = buildFocusDayTimelineRows(
				normalizedEntries,
				summaryRows,
				filters.focusDate
			);

			set({
				teamMembers: Array.isArray(members) ? members : [],
				entries: normalizedEntries,
				summaryRows,
				timelineRows,
				hydrated: true,
				loading: false,
				error: "",
			});
		} catch (error) {
			set({
				entries: [],
				summaryRows: [],
				timelineRows: [],
				hydrated: false,
				loading: false,
				error: error?.message || "Failed to load time clock records",
			});
		}
	},

	reset: () => {
		set({
			filters: createDefaultFilters(),
			teamMembers: [],
			entries: [],
			summaryRows: [],
			timelineRows: [],
			hydrated: false,
			loading: false,
			error: "",
		});
	},
}));

export { TIME_RANGE_PRESETS };
export default useAdminTimeClockStore;
